import {Separator} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {SourcingPriceResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';

interface PriceTableProps {
  originalPrice: number;
  price: SourcingPriceResponseBody;
}

/** The value grows and is right-aligned so a wrapped row does not left-align it. */
export function PriceTable(props: PriceTableProps) {
  const formatPrice = useFormatCurrency();
  const currency = useSourcingCurrency();

  const priceWithVat = props.price.originalWithVat ?? 0;
  const vatRate = props.price.vatRate ?? 0;

  const priceWithoutVat = priceWithVat > 0 ? (priceWithVat / (100 + vatRate)) * 100 : 0;
  const priceLabel = i18n.t(
    props.price.vatReclaimable
      ? 'entity.sourcing.vehiclePreview.labels.priceWithVat'
      : 'general.labels.price'
  );

  return (
    <VStack data-testid={testIds.sourcing.classifieds('priceTable')}>
      <HStack
        justify="space-between"
        align="center"
        spacing={2}
        wrap
        data-testid={testIds.sourcing.classifieds('priceTableRow-1')}
      >
        <Text size="xSmall">{priceLabel}</Text>
        <Box flexGrow={1}>
          <Heading size={2} align="right" isSingleLine>
            {formatPrice(props.originalPrice, currency?.code ?? DEFAULT_CURRENCY_CODE, 0)}
          </Heading>
        </Box>
      </HStack>
      <Separator spacing={2} />
      <HStack
        justify="space-between"
        align="center"
        wrap
        data-testid={testIds.sourcing.classifieds('priceTableRow-2')}
      >
        <Text size="xSmall">
          {`${priceLabel} (${i18n.t('entity.sourcing.vehiclePreview.labels.inOriginalCurrency')})`}
        </Text>
        <Box flexGrow={1}>
          <Text size="small" align="right" noWrap>
            {formatPrice(
              priceWithVat,
              props.price.originalCurrency?.key ?? DEFAULT_CURRENCY_CODE,
              0
            )}
          </Text>
        </Box>
      </HStack>
      <Separator spacing={2} />
      <Show when={props.price.vatReclaimable}>
        <HStack
          justify="space-between"
          align="center"
          wrap
          data-testid={testIds.sourcing.classifieds('priceTableRow-3')}
        >
          <Text size="xSmall">
            {`${i18n.t('entity.sourcing.vehiclePreview.labels.priceWithoutVat')} (${i18n.t(
              'entity.sourcing.vehiclePreview.labels.inOriginalCurrency'
            )})`}
          </Text>
          <Box flexGrow={1}>
            <Text size="small" align="right" noWrap alternative>
              {formatPrice(
                priceWithoutVat,
                props.price.originalCurrency?.key ?? DEFAULT_CURRENCY_CODE,
                0
              )}
            </Text>
          </Box>
        </HStack>
        <Separator spacing={2} />
      </Show>
      <HStack
        justify="space-between"
        wrap
        data-testid={testIds.sourcing.classifieds('priceTableRow-4')}
      >
        <Text size="xSmall">{i18n.t('entity.sourcing.vehiclePreview.labels.vatDeductible')}</Text>
        <Box flexGrow={1}>
          <Text size="small" align="right" noWrap>
            {props.price.vatReclaimable
              ? i18n.t('general.labels.yes')
              : i18n.t('general.labels.no')}
          </Text>
        </Box>
      </HStack>
    </VStack>
  );
}
